import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function PaymentSuccess() {
  const [sp] = useSearchParams();
  const session_id = sp.get("session_id");
  const [status, setStatus] = useState("polling");
  const attempts = useRef(0);
  const { refresh } = useAuth();

  useEffect(() => {
    if (!session_id) { setStatus("error"); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${session_id}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refresh();
          return;
        }
        if (["failed", "expired", "refunded"].includes(data.payment_status)) {
          setStatus("failed"); return;
        }
      } catch {}
      attempts.current += 1;
      if (attempts.current > 15) { setStatus("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [session_id, refresh]);

  return (
    <div className="max-w-xl mx-auto px-4 py-24 text-center">
      {status === "polling" && (
        <>
          <div className="h-10 w-10 rounded-full border-2 border-amber-500/30 border-t-amber-400 animate-spin mx-auto mb-6" />
          <h1 className="font-serif text-3xl font-bold mb-2">Confirming your subscription…</h1>
          <p className="text-slate-400">One moment while we unlock the archive.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto mb-4" />
          <h1 data-testid="payment-success" className="font-serif text-3xl md:text-4xl font-bold mb-2">You're in. Welcome, reader.</h1>
          <p className="text-slate-400 mb-8">The full ProSeCraft library is now yours. New stories drop weekly.</p>
          <div className="flex gap-3 justify-center">
            <Button asChild className="bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-full h-12 px-7">
              <Link to="/stories">Open the library</Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full h-12 px-7 border-white/15">
              <Link to="/dashboard">Go to dashboard</Link>
            </Button>
          </div>
        </>
      )}
      {(status === "failed" || status === "timeout" || status === "error") && (
        <>
          <XCircle className="h-12 w-12 text-rose-400 mx-auto mb-4" />
          <h1 className="font-serif text-3xl font-bold mb-2">Something went sideways</h1>
          <p className="text-slate-400 mb-8">Your card was not charged. Try again from the pricing page.</p>
          <Button asChild variant="outline" className="rounded-full h-12 px-7 border-white/15">
            <Link to="/pricing">Back to pricing</Link>
          </Button>
        </>
      )}
    </div>
  );
}
